/**
 * MRCA (most recent common ancestor) lineage tree for autosomal DNA attribution.
 * Binary tree: M = maternal branch, P = paternal branch at each generation.
 * Depth 1 = parents (50% each); depth 6 ≈ 1.5625% per ancestor (simplified model).
 */

import { DEFAULT_HOME_PERSON_ID } from './homePersonStorage';

export const MAX_MRCA_DEPTH = 6;

/** Dispatched on window after MRCA map state is saved (same tab). */
export const MRCA_STATE_CHANGED_EVENT = 'geneology-mrca-persisted';

/** Floating-point epsilon for theoretical sums. */
export const THEORETICAL_EPSILON = 0.0001;

/** Expected % of genome from a single ancestor at this depth (depth 1 = parents). */
export function theoreticalPercentForPath(path: string): number {
  const d = path.length;
  if (d === 0) return 100;
  return 100 * Math.pow(0.5, d);
}

/** All lineage paths from depth 1 through maxDepth (inclusive). e.g. M, P, MM, MP, ... */
export function enumerateMrcaPaths(maxDepth: number): string[] {
  const result: string[] = [];
  function rec(prefix: string) {
    if (prefix.length >= maxDepth) return;
    const m = prefix + 'M';
    const p = prefix + 'P';
    result.push(m, p);
    rec(m);
    rec(p);
  }
  rec('');
  return result;
}

export function leafPaths(maxDepth: number): string[] {
  return enumerateMrcaPaths(maxDepth).filter((path) => path.length === maxDepth);
}

/** Sum of theoretical shares for every node at exactly `depth` (should always be ~100%). */
export function sumTheoreticalAtDepth(depth: number, maxDepth: number): number {
  return enumerateMrcaPaths(maxDepth)
    .filter((path) => path.length === depth)
    .reduce((sum, path) => sum + theoreticalPercentForPath(path), 0);
}

/** Sum of theoretical shares for all leaves (should be ~100%). */
export function sumLeafTheoreticalPercent(maxDepth: number): number {
  return leafPaths(maxDepth).reduce((sum, path) => sum + theoreticalPercentForPath(path), 0);
}

export function validateTheoreticalModel(maxDepth: number): {
  ok: boolean;
  perDepth: { depth: number; sum: number }[];
} {
  const perDepth: { depth: number; sum: number }[] = [];
  for (let d = 1; d <= maxDepth; d += 1) {
    const sum = sumTheoreticalAtDepth(d, maxDepth);
    perDepth.push({ depth: d, sum });
  }
  const ok = perDepth.every(
    (row) => Math.abs(row.sum - 100) < THEORETICAL_EPSILON
  );
  return { ok, perDepth };
}

/** Each child's theoretical share should equal half of parent's (for display checks). */
export function childPaths(path: string, maxDepth: number): string[] {
  if (path.length >= maxDepth) return [];
  return [path + 'M', path + 'P'];
}

/** Legacy: single key before home-person–scoped storage. Migrated when loading default home id. */
export const MRCA_STORAGE_KEY_LEGACY = 'geneology-mrca-lineage-v1';

/** Persisted MRCA map state per home person (DNA test subject). */
export function mrcaStorageKey(homeId: string): string {
  return `geneology-mrca-lineage-v1-${homeId}`;
}

export interface MrcaPersistedState {
  displayNames: Record<string, string>;
  /** Individual id from the family tree (GET /individuals), keyed by MRCA path. */
  linkedIndividualIds: Record<string, string>;
  /** Optional manual cM per path (segments not in DNA Painter yet, or notes). */
  manualAssignedCm: Record<string, number | undefined>;
  notes: Record<string, string>;
}

export function defaultMrcaPersistedState(): MrcaPersistedState {
  return { displayNames: {}, linkedIndividualIds: {}, manualAssignedCm: {}, notes: {} };
}

export function loadMrcaPersistedState(homeId: string): MrcaPersistedState {
  try {
    let raw = localStorage.getItem(mrcaStorageKey(homeId));
    if (!raw && homeId === DEFAULT_HOME_PERSON_ID) {
      raw = localStorage.getItem(MRCA_STORAGE_KEY_LEGACY);
    }
    if (!raw) return defaultMrcaPersistedState();
    const parsed = JSON.parse(raw) as MrcaPersistedState;
    return {
      displayNames: parsed.displayNames || {},
      linkedIndividualIds: parsed.linkedIndividualIds || {},
      manualAssignedCm: parsed.manualAssignedCm || {},
      notes: parsed.notes || {},
    };
  } catch {
    return defaultMrcaPersistedState();
  }
}

export function saveMrcaPersistedState(homeId: string, state: MrcaPersistedState): void {
  try {
    localStorage.setItem(mrcaStorageKey(homeId), JSON.stringify(state));
    window.dispatchEvent(new CustomEvent(MRCA_STATE_CHANGED_EVENT, { detail: { homeId } }));
  } catch {
    /* ignore quota */
  }
}

const MRCA_VIZ_PALETTE = [
  '#43a047', '#fbc02d', '#0288d1', '#8e24aa', '#ff7043', '#1b5e20', '#e91e63', '#2196f3', '#455a64', '#ffb300',
  '#6d4c41', '#00bcd4', '#c62828', '#ad1457', '#2e7d32', '#f57c00', '#5d4037', '#1976d2', '#7b1fa2', '#0097a7',
  '#d84315', '#388e3c', '#f06292', '#00838f', '#d32f2f', '#7e57c2', '#689f38', '#ffa000', '#303f9f', '#ff5722',
  '#607d8b', '#795548', '#00acc1', '#f44336', '#8bc34a', '#ff9800', '#3f51b5', '#9c27b0', '#4caf50', '#e65100',
  '#1a237e', '#00e676', '#ff1744', '#00bfae', '#d500f9', '#ffea00', '#00c853', '#ff6d00', '#aeea00', '#6200ea',
  '#ffd600', '#00b8d4', '#ffab00', '#304ffe', '#c51162', '#64dd17', '#ff3d00', '#0091ea',
];

/** Stable colour per MRCA path code for chromosome bars and legends. */
export function buildMrcaPathColorMap(): Record<string, string> {
  const paths = enumerateMrcaPaths(MAX_MRCA_DEPTH).sort(
    (a, b) => a.length - b.length || a.localeCompare(b)
  );
  const map: Record<string, string> = {};
  for (let i = 0; i < paths.length; i += 1) {
    map[paths[i]] = MRCA_VIZ_PALETTE[i % MRCA_VIZ_PALETTE.length];
  }
  return map;
}
